/**
 * GenerateTopicsDialog — Stage A dialog.
 *
 * Uses the canonical CompositeDataPicker + useCompositeDataOrchestrator
 * (same stack as StrategyFormDialog) so audiences/tasks/reviews/… load
 * automatically from Firestore with no extra wiring.
 *
 * Drives POST /api/agent/content-plan/generate-topics-batch.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Stack, TextField, Typography, Box, Alert, FormControl, InputLabel,
  Select, MenuItem, Slider, Chip, Switch, FormControlLabel,
} from '@mui/material';
import { BaseDialog, DialogHeader, DialogContent, DialogFooter } from '@modules/ui-components';
import { AvailableModelSelector } from '@modules/ai-settings';
import { CompositeDataPicker } from '@modules/data-pickers';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { useCompositeDataOrchestrator } from '@modules/strategy-blocks/hooks/useCompositeDataOrchestrator';
import { ContentPlanApiClient } from '../services/ContentPlanApiClient';
import { useThemeCatalog } from '../hooks/useThemeCatalog';
import { usePillars } from '../hooks/usePillars';
import { ThemeCatalogDialog } from './ThemeCatalogDialog';
import { useProjectDefaultModel } from '../hooks/useProjectDefaultModel';
import SettingsIcon from '@mui/icons-material/Settings';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// Inline testid literals — kit-registry keeps matching strings for E2E tests.
const CONTENT_PLAN = {
  GEN_TOPICS_DIALOG: 'generate-topics-dialog',
  COUNT: 'gen-topics-count',
  THEMES: 'gen-topics-themes',
  PILLARS: 'gen-topics-pillars',
  AUTHOR_TOGGLE: 'gen-topics-author-toggle',
  DESCRIPTION: 'gen-topics-description',
  RESEARCH_MODE: 'gen-topics-research-mode',
  MODEL_TOPICGEN: 'gen-topics-model-tg',
  MODEL_RESEARCH: 'gen-topics-model-research',
  SUBMIT: 'gen-topics-submit',
  CANCEL: 'gen-topics-cancel',
} as const;

export interface GenerateTopicsDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  userId: string;
  authorId: string;
  strategyService: StrategyService;
  /** Optional initial filter to pin themes (e.g. sprint gap-fill) */
  initialThemeIds?: string[];
  themeCatalog?: Array<{ id: string; name: string }>;
  onSuccess?: (result: { taskId: string; briefId?: string }) => void;
}

// Default themes used when project has no ThemeCatalog doc yet.
// Mirrors DEFAULT_THEME_CATALOG in StrategyPlanningOrchestrator so backend
// always recognises them.
const DEFAULT_THEMES = [
  { id: 'educational', name: 'Educational' },
  { id: 'storytelling', name: 'Storytelling' },
  { id: 'social_proof', name: 'Social Proof' },
  { id: 'contrarian', name: 'Contrarian Take' },
  { id: 'behind_scenes', name: 'Behind the Scenes' },
  { id: 'promo', name: 'Promotional' },
  { id: 'curated_or_trend', name: 'Curated / Trend' },
];

export const GenerateTopicsDialog: React.FC<GenerateTopicsDialogProps> = ({
  open, onClose, projectId, userId, strategyService,
  authorId,
  initialThemeIds = [], themeCatalog, onSuccess,
}) => {
  // Live-subscribe to projects/{pid}/catalogs/theme_catalog. When user edits
  // themes via ThemeCatalogDialog the dropdown below updates immediately.
  const { themes: firestoreThemes } = useThemeCatalog(projectId);
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);

  // Phase B — confirmed pillars feed the multi-select picker.
  const { pillars: allPillars } = usePillars(projectId);
  const confirmedPillars = useMemo(
    () => allPillars.filter((p) => !p.isDraft),
    [allPillars],
  );

  // Precedence: Firestore catalog > passed prop > built-in defaults.
  const effectiveThemes = firestoreThemes && firestoreThemes.length > 0
    ? firestoreThemes.map((t) => ({ id: t.id, name: t.name ?? t.id }))
    : (themeCatalog && themeCatalog.length > 0 ? themeCatalog : DEFAULT_THEMES);
  const [count, setCount] = useState<number>(20);
  const [themeIds, setThemeIds] = useState<string[]>(initialThemeIds);
  const [selectedPillarIds, setSelectedPillarIds] = useState<string[]>([]);
  const [includeAuthor, setIncludeAuthor] = useState<boolean>(true);
  const [pillarsTouched, setPillarsTouched] = useState<boolean>(false);
  // Mode B (pillar-first generation): when ON, the orchestrator fans out one
  // topic batch per selected pillar. Audience becomes "for whom" tag; pillars
  // are the topic source. See modules/content-plan/architecture/
  // pillar-theme-strategicAngle-spec.md "Mode A vs Mode B".
  const [pillarFirstMode, setPillarFirstMode] = useState<boolean>(false);
  const [userDescription, setUserDescription] = useState('');
  const [keywordsText, setKeywordsText] = useState('');
  const [avoidText, setAvoidText] = useState('');
  const [researchMode, setResearchMode] = useState<'none' | 'use_existing' | 'run_fresh' | 'hybrid'>('none');
  const [modelTopicGen, setModelTopicGen] = useState<string>(projectDefaultModel.value);
  const [modelResearch, setModelResearch] = useState(projectDefaultModel.value);
  const [selectedPaths, setSelectedPaths] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Pre-select all confirmed pillars on first open. We track "touched" so a
  // user who explicitly clears the selection doesn't get re-selected when
  // confirmedPillars stream re-emits. Resets when dialog closes (open→false).
  useEffect(() => {
    if (!open) return;
    setModelTopicGen(projectDefaultModel.value);
    setModelResearch(projectDefaultModel.value);
  }, [open, projectDefaultModel.value]);

  useEffect(() => {
    if (!open) {
      setPillarsTouched(false);
      return;
    }
    if (pillarsTouched) return;
    if (confirmedPillars.length === 0) return;
    setSelectedPillarIds(confirmedPillars.map((p) => p.id));
  }, [open, confirmedPillars, pillarsTouched]);

  // Same composite data orchestrator the onboarding Strategy dialog uses —
  // auto-loads audiences/tasks/reviews/…, converts to sourceDataIds + hydrated.
  const orchestrator = useCompositeDataOrchestrator({
    projectId,
    strategyService,
    initialSelection: selectedPaths,
    selectAll: false,
    onSelectionChange: (d) => setSelectedPaths(d.selectedIds),
    open,
  });

  // Themes are OPTIONAL — backend has a preset theme distribution fallback.
  // Submit is blocked only on count=0.
  const canSubmit = count > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const client = new ContentPlanApiClient();
      const response = await client.generateTopicsBatch({
        brief: {
          id: `brief_${Date.now().toString(36)}`,
          projectId, userId, authorId,
          count,
          // Send only a non-empty themeIds array; backend preset fires otherwise.
          ...(themeIds.length > 0 ? { themeIds } : {}),
          userDescription: userDescription.trim() || undefined,
          userKeywords: keywordsText.split(',').map((s) => s.trim()).filter(Boolean),
          userAvoidTopics: avoidText.split(',').map((s) => s.trim()).filter(Boolean),
          researchMode,
        } as any,
        sourceData: orchestrator.hydratedData,
        sourceDataIds: orchestrator.aiSourceDataIds as any,
        themeCatalog: effectiveThemes,
        models: { topicGenerator: modelTopicGen, research: modelResearch },
        // Phase B — explicit pillar/author selection. The backend's 3-state
        // contract (see TopicBacklogInput.author/.topicPillars JSDoc):
        //   selectedPillarIds = []     → opt-out of pillars (skip pillarIds emission)
        //   selectedPillarIds = [...]  → restrict to subset
        //   field absent               → orchestrator auto-loads (UI never sends absent)
        // We always send the array because the picker materially exists in the UI.
        selectedPillarIds,
        includeAuthor,
        authorId,
        pillarInfluence: pillarFirstMode ? { topicSourcing: 1.0 } : undefined,
      } as any);
      if (!response.success) throw new Error(response.error ?? 'Generation failed');
      onSuccess?.({ taskId: response.taskId, briefId: response.briefId });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseDialog open={open} onClose={onClose} maxWidth="md" data-testid={CONTENT_PLAN.GEN_TOPICS_DIALOG}>
      <DialogHeader title="Generate topics into backlog" onClose={onClose} />
      <DialogContent>
        <Stack spacing={2.5} sx={{ py: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Typography variant="subtitle2" gutterBottom>Count: {count}</Typography>
            <Slider
              value={count}
              onChange={(_, v) => setCount(v as number)}
              step={1} min={5} max={50}
              marks={[{ value: 5 }, { value: 20 }, { value: 50 }]}
              valueLabelDisplay="auto"
              data-testid={CONTENT_PLAN.COUNT}
            />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <FormControl fullWidth size="small" data-testid={CONTENT_PLAN.THEMES}>
                <InputLabel>Themes ({themeIds.length})</InputLabel>
                <Select
                  multiple
                  value={themeIds}
                  label={`Themes (${themeIds.length})`}
                  onChange={(e) => setThemeIds(
                    typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                  )}
                  renderValue={(vals) => (vals as string[]).map((id) => (
                    <Chip key={id} size="small" label={effectiveThemes.find((t) => t.id === id)?.name ?? id} sx={{ mr: 0.5 }} />
                  ))}
                >
                  {effectiveThemes.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Manage project themes (add / edit / AI-generate)">
                <IconButton
                  size="small"
                  onClick={() => setThemeDialogOpen(true)}
                  sx={{ mt: 0.5 }}
                  data-testid="gen-topics-open-theme-catalog"
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Optional — leave empty and the backend picks a preset theme mix based on content-mix config.
              {firestoreThemes.length === 0 && ' (using default themes — click ⚙ to create a project ThemeCatalog)'}
            </Typography>
          </Box>

          <TextField
            label="What should these topics be about?"
            multiline minRows={3}
            value={userDescription}
            onChange={(e) => setUserDescription(e.target.value)}
            placeholder="e.g. Focus on Q1 launch learnings, data-driven tone, emphasise counterintuitive findings."
            inputProps={{ 'data-testid': CONTENT_PLAN.DESCRIPTION }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Keywords (comma-separated)"
              value={keywordsText}
              onChange={(e) => setKeywordsText(e.target.value)}
              fullWidth size="small"
            />
            <TextField
              label="Avoid topics (comma-separated)"
              value={avoidText}
              onChange={(e) => setAvoidText(e.target.value)}
              fullWidth size="small"
            />
          </Box>

          {/* Phase B — Author + Pillars context selectors.
              When confirmed pillars exist, default = all selected + author ON.
              Empty selection allowed — topics will be generic without pillarIds.
              No pillars confirmed yet → show inline hint, hide picker. */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={includeAuthor}
                  onChange={(e) => setIncludeAuthor(e.target.checked)}
                  size="small"
                  inputProps={{ 'data-testid': CONTENT_PLAN.AUTHOR_TOGGLE } as any}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Include author context</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Positioning, expertise, UVP, values, origin story.
                    {!includeAuthor && ' Disabled — topics will be generic, not grounded in your brand voice.'}
                  </Typography>
                </Box>
              }
            />
          </Box>

          {confirmedPillars.length > 0 && (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={pillarFirstMode}
                    onChange={(e) => setPillarFirstMode(e.target.checked)}
                    size="small"
                    inputProps={{ 'data-testid': 'gen-topics-pillar-first-mode' } as any}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Generate from pillars (Mode B)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Treat selected pillars as topic sources. Default OFF: pillars are a secondary lens (Mode A audience-first).
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}

          {confirmedPillars.length > 0 ? (
            <Box>
              <FormControl fullWidth size="small" data-testid={CONTENT_PLAN.PILLARS}>
                <InputLabel>Pillars ({selectedPillarIds.length}/{confirmedPillars.length})</InputLabel>
                <Select
                  multiple
                  value={selectedPillarIds}
                  label={`Pillars (${selectedPillarIds.length}/${confirmedPillars.length})`}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedPillarIds(typeof v === 'string' ? v.split(',') : (v as string[]));
                    setPillarsTouched(true);
                  }}
                  renderValue={(vals) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(vals as string[]).map((id) => {
                        const p = confirmedPillars.find((pp) => pp.id === id);
                        return (
                          <Chip
                            key={id}
                            size="small"
                            label={p?.name ?? id}
                            data-testid={`gen-topics-pillar-chip-${id}`}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {confirmedPillars.map((p) => (
                    <MenuItem
                      key={p.id}
                      value={p.id}
                      data-testid={`gen-topics-pillar-${p.id}`}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>{p.name}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Topics will be tagged with their primary pillar and grouped on the Backlog.
                Empty selection = generic topics without <code>pillarIds</code>.
              </Typography>
            </Box>
          ) : (
            <Alert severity="info" variant="outlined">
              No confirmed pillars yet. Topics will be generated without pillar tags.
              Confirm pillars in <strong>Creator → Topic Pillars</strong> first if you want pillar grouping.
            </Alert>
          )}

          <Box data-testid="source-data-picker">
            <Typography variant="subtitle2" gutterBottom>Source Data</Typography>
            <CompositeDataPicker
              {...orchestrator.compositeProps}
              pickers={[
                { type: 'audience' as const, label: 'Audiences', enabled: true, selectAll: false },
                { type: 'task' as const, label: 'Tasks', enabled: true, selectAll: false },
                { type: 'socialproof' as const, label: 'Social Proof', enabled: true, selectAll: false },
                { type: 'insights' as const, label: 'Insights', enabled: true, selectAll: false },
              ]}
            />
          </Box>

          <FormControl fullWidth size="small" data-testid={CONTENT_PLAN.RESEARCH_MODE}>
            <InputLabel>Research mode</InputLabel>
            <Select
              value={researchMode}
              label="Research mode"
              onChange={(e) => setResearchMode(e.target.value as typeof researchMode)}
            >
              <MenuItem value="none">None — skip research</MenuItem>
              <MenuItem value="use_existing">Use existing research packs</MenuItem>
              <MenuItem value="run_fresh">Run fresh grounded search (costs Gemini tokens)</MenuItem>
              <MenuItem value="hybrid">Hybrid — use existing + run fresh</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <AvailableModelSelector
              projectId={projectId}
              value={modelTopicGen}
              onChange={setModelTopicGen}
              label="Topic generator"
              sx={{ flex: 1, minWidth: 0, width: '100%' }}
            />
            {researchMode !== 'none' && (
              <AvailableModelSelector
                projectId={projectId}
                value={modelResearch}
                onChange={setModelResearch}
                label="Research model"
                sx={{ flex: 1, minWidth: 0, width: '100%' }}
              />
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogFooter
        secondaryAction={{
          label: 'Cancel', onClick: onClose, 'data-testid': CONTENT_PLAN.CANCEL,
        } as any}
        primaryAction={{
          label: submitting ? 'Submitting…' : 'Generate',
          onClick: handleSubmit,
          disabled: !canSubmit || submitting,
          'data-testid': CONTENT_PLAN.SUBMIT,
        } as any}
      />

      {/* Theme-catalog editor — AI-seed + manual CRUD.
          Owns its own CompositeDataPicker, so we pass strategyService only. */}
      <ThemeCatalogDialog
        open={themeDialogOpen}
        onClose={() => setThemeDialogOpen(false)}
        projectId={projectId}
        userId={userId}
        strategyService={strategyService}
      />
    </BaseDialog>
  );
};

export default GenerateTopicsDialog;
